/**
 * ENG-8344 — broker-delivered GitHub App credentials for `git` and `gh`.
 *
 * The problem
 * -----------
 * A GitHub App installation token has a ~1h TTL. `POST /host/agent-integrations`
 * mints one per poll and the claudecode adapter materializes it into
 * `.env.integrations` as `GITHUB_ACCESS_TOKEN` + `GITHUB_TOKEN` (ENG-7506).
 * The persistent wrapper sources that file EXACTLY ONCE at spawn, so the only
 * way a running agent ever sees a rotated token is a full session respawn —
 * a guaranteed hourly session kill for every agent holding a GitHub App
 * install (measured: 6 agents, ~144 kills/day). ENG-8343 stopped those kills
 * landing mid-turn; this module removes them.
 *
 * The shape of the fix
 * --------------------
 * Two small programs written into the agent's own bin dir:
 *
 *   * `git-credential-agt-github` — a git credential helper. git runs it at
 *     USE time (per fetch/push), it fetches the current token from the broker
 *     and writes it back over git's credential protocol on stdout.
 *   * `gh` — a PATH shim. Resolves the same token, then runs the REAL `gh`
 *     with it in that child's environment only.
 *
 * Neither ever writes the token to disk. A design review explicitly rejected
 * the token-file variant: it moves a live bearer token from RAM onto disk
 * where any process running as that user — and any backup or log snapshotting
 * the directory — can read it. Fetch at use time, hand it over a pipe, forget it.
 *
 * What this does and does NOT buy
 * -------------------------------
 * It removes the token from the agent process ENVIRONMENT, which is the thing
 * that forces the respawns. It does NOT make the token unreachable by a
 * determined agent: the agent can run the helper itself, exactly as git does.
 * That is still strictly better than today (no rotation-driven session kill, no
 * hour-old token in every child process's environ) but it is not a sandbox and
 * must not be described as one.
 *
 * Addressing
 * ----------
 * The broker credential endpoint is reached by `definition_id` (`github`)
 * rather than the integration uuid — ENG-8264 / PR #3949. That matters: the
 * uuid is only ever set inside `.mcp.json` MCP env blocks and never reaches the
 * agent process env, whereas `AGT_HOST` / `AGT_API_KEY` / `AGT_AGENT_ID` are
 * already there. Without name addressing these programs could not call the
 * endpoint at all.
 *
 * That last claim was HALF WRONG when written, and cost real downtime. `AGT_HOST`
 * and `AGT_API_KEY` are host-wide (written by `agt setup`) and so are inherited
 * by any agent process. `AGT_AGENT_ID` is per-agent and had exactly one writer:
 * the `-e AGT_AGENT_ID=<uuid>` forward on the docker argv. Isolated agents were
 * fine; every BARE-HOST agent ran with it unset, and since these programs read
 * the process env directly they hard-exited on `gh` and on every `git
 * fetch`/`push` — while the broker, the token endpoint and the GitHub App
 * install were all healthy, which sends an operator hunting in the wrong place.
 *
 * `buildSpawnEnv` in apps/cli/src/lib/persistent-session.ts now stamps it on the
 * bare-host path too, so the assumption above finally holds on both paths. It is
 * enforced rather than assumed: `REQUIRED_SPAWN_ENV` / `findMissingSpawnEnv`
 * assert it at spawn and log a loud line naming this shim as the casualty.
 *
 * Then it broke a SECOND time, in the way that assertion cannot see. The spawn
 * check only asks whether `AGT_AGENT_ID` is non-empty; a present-but-WRONG uuid
 * passes it. An agent ran with another agent's id in its process env while its
 * own `.mcp.json` carried the right one, so the broker was asked for a github
 * integration belonging to somebody else and answered `404 Integration not
 * found`. Read literally that says "your GitHub is disconnected", and it sent
 * the investigation at the App install and the broker, both of which were
 * healthy. `identityMismatchHint` below closes that gap: on a `404` from the
 * credential fetch the program compares the id it used against the ids in this
 * agent's own `.mcp.json` and, when they disagree, says so in one sentence. It
 * reports and does not self-heal - see the comment on that function for why
 * substituting the file's value would be a cross-agent privilege escalation,
 * and for why it fires on a 404 and on nothing else.
 */

/** Directory (under the agent's project dir) holding the helper + shim. */
export const GITHUB_BROKER_BIN_DIR = '.claude/agt-bin';

/** Basename of the git credential helper. */
export const GIT_CREDENTIAL_HELPER_BASENAME = 'git-credential-agt-github';

/**
 * Basename of the `gh` shim. It MUST be exactly `gh`: the shim works by
 * shadowing the real binary on PATH, and the wrapper prepends this directory.
 */
export const GH_SHIM_BASENAME = 'gh';

/** The broker credential endpoint, addressed by definition_id (ENG-8264). */
export const GITHUB_CREDENTIAL_PATH = '/host/agent-integrations/github/credential';

/** Mode for both programs: owner read/write/execute only. */
export const BROKER_SCRIPT_MODE = 0o700;

/**
 * The env vars that point `git` at the credential helper.
 *
 * `GIT_CONFIG_COUNT`/`_KEY_n`/`_VALUE_n` (git >= 2.31) inject config into a
 * single process's environment rather than mutating `~/.gitconfig`. That keeps
 * the change per-agent, invisible to anything else running as the same user,
 * and removed the moment the flag goes off — no file to clean up, nothing left
 * behind on a rollback.
 *
 * Two entries for one key, deliberately:
 *   * index 0 sets `credential.https://github.com.helper` to the EMPTY string,
 *     which is git's documented idiom for "reset the helper list for this URL".
 *     Without it a helper already configured in `~/.gitconfig` (a `store` file,
 *     a stale `gh auth git-credential`) would be consulted first and could win
 *     with an expired token — the exact failure we are removing.
 *   * index 1 installs ours as the only helper for github.com.
 *
 * `GIT_TERMINAL_PROMPT=0` is set with them. Under tmux the agent HAS a tty, so
 * a credential miss would otherwise leave `git push` blocked on a Username:
 * prompt forever. Failing immediately is the legible outcome (constraint D).
 *
 * Known limitation, stated rather than hidden: an agent command that sets its
 * own `GIT_CONFIG_COUNT` replaces these. Nothing in the fleet does today.
 */
export function buildGitCredentialEnv(helperPath: string): Record<string, string> {
  return {
    GIT_CONFIG_COUNT: '2',
    GIT_CONFIG_KEY_0: 'credential.https://github.com.helper',
    GIT_CONFIG_VALUE_0: '',
    GIT_CONFIG_KEY_1: 'credential.https://github.com.helper',
    GIT_CONFIG_VALUE_1: helperPath,
    GIT_TERMINAL_PROMPT: '0',
  };
}

/** Every env key this module owns — used to prune them when the flag is off. */
export const GITHUB_BROKER_ENV_KEYS: readonly string[] = Object.keys(
  buildGitCredentialEnv(''),
);

/**
 * Shared prelude: resolve a fresh installation token from the broker.
 *
 * Written as Node rather than shell on purpose. The shell version needs `jq`
 * (not guaranteed in every agent image) or hand-rolled JSON scraping that
 * silently mis-parses a `null` token into the literal string "null" — which
 * would present as an authentication failure against GitHub instead of a
 * credential-fetch failure, sending whoever debugs it at the wrong system.
 * Node is present wherever Claude Code runs.
 *
 * Two calls: `POST /host/exchange` (api key -> short JWT) then the credential
 * endpoint. The JWT is deliberately NOT cached to disk — it is a bearer token,
 * and the whole point of this change is to stop parking bearer tokens where a
 * later reader can find them. The cost is one extra round trip per `git push`.
 */
const TOKEN_RESOLVER_PRELUDE = `
const AGT_HOST = (process.env.AGT_HOST || '').replace(/\\/+$/, '');
const AGT_API_KEY = process.env.AGT_API_KEY || '';
const AGT_AGENT_ID = process.env.AGT_AGENT_ID || '';
const SELF_FILE = __filename;

/**
 * The agent ids the manager wrote into THIS agent's own .mcp.json.
 *
 * This program lives at <projectDir>/.claude/agt-bin/<name>, so the project dir
 * is three dirnames up. Best-effort and never fatal: an agent with no .mcp.json,
 * an unreadable file or a relocated bin dir yields [] and the caller stays quiet.
 * Scraped with a regex rather than JSON.parse because the file is a rendered
 * config that may carry unsubstituted variable placeholders in other fields, and
 * a parse failure there must not swallow the identity we came for.
 */
function mcpConfigAgentIds() {
  try {
    const { readFileSync } = require('node:fs');
    const { dirname, join } = require('node:path');
    const projectDir = dirname(dirname(dirname(SELF_FILE)));
    const raw = readFileSync(join(projectDir, '.mcp.json'), 'utf8');
    const ids = [];
    const re = /"AGT_AGENT_ID"\\s*:\\s*"([0-9a-fA-F-]{8,})"/g;
    let m;
    while ((m = re.exec(raw)) !== null) {
      if (ids.indexOf(m[1]) === -1) ids.push(m[1]);
    }
    return ids;
  } catch {
    return [];
  }
}

/**
 * Extra sentence for the "Integration not found" case, which is the single most
 * misleading response this program can get: it reads as "GitHub is disconnected"
 * when the real cause can be that the PROCESS env carries a different agent's
 * uuid than the one this agent actually is. That happened in the field - the
 * .mcp.json blocks held the right id (they are rendered per agent) while the
 * process env held a stale one inherited from elsewhere, so the broker was asked
 * for an integration belonging to somebody else and answered, correctly, 404.
 *
 * ONLY ON A 404, and the gate is the point rather than a tidy-up. The sentence
 * below does not hedge - it asserts that the broker "answered not found" and
 * that "your GitHub integration is probably fine". On a 500 both halves are
 * false: the broker answered nothing, and the state of the integration is
 * unknown. It used to be appended to EVERY credential-fetch failure, so during
 * the broker outage of 2026-08-20 any agent whose process env id disagreed with
 * its .mcp.json would have been told, in confident prose, to go and fix its
 * provisioner while the actual fault was an outage it had no part in. A
 * diagnostic that is right about a 404 and wrong about a 500 is worse than no
 * diagnostic, because it is believed.
 *
 * DIAGNOSTIC ONLY. It deliberately does NOT fall back to the .mcp.json value.
 * The broker authorises with the HOST key, which covers every agent on the host,
 * so honouring an agent-writable file as the identity would let any agent mint
 * another agent's GitHub token. The process env stays the only accepted source;
 * a mismatch is something an operator fixes at the provisioner.
 */
function identityMismatchHint(status) {
  if (status !== 404) return '';
  const ids = mcpConfigAgentIds();
  if (ids.length === 0 || ids.indexOf(AGT_AGENT_ID) !== -1) return '';
  return (
    '\\naugmented: IDENTITY MISMATCH - process.env.AGT_AGENT_ID is ' + AGT_AGENT_ID +
    ', but this agent\\'s own .mcp.json says ' + ids.join(', ') + '. The broker was ' +
    'asked for an integration belonging to a DIFFERENT agent, which is why it ' +
    'answered "not found". Your GitHub integration is probably fine. Fix the ' +
    'agent id the session was spawned with; this program will not use the ' +
    '.mcp.json value, because that file is agent-writable and the broker ' +
    'authorises with the host key.'
  );
}

/**
 * Every failure exits through here, so an operator always gets a sentence that
 * names the credential fetch as the failing step. A bare 401 from GitHub sends
 * people auditing App permissions; "broker credential fetch failed: 503" sends
 * them at the API. Constraint D of ENG-8344.
 *
 * The agent id is echoed on every failure. It is not a secret, and a 404 whose
 * message does not say WHICH agent was looked up is unfalsifiable from the
 * operator's side.
 */
function fail(reason) {
  process.stderr.write(
    'augmented: GitHub credential fetch failed: ' + reason + '\\n' +
    'augmented: the token is fetched per use from ' + (AGT_HOST || '<AGT_HOST unset>') +
    '${GITHUB_CREDENTIAL_PATH}' + ' for agent_id=' + (AGT_AGENT_ID || '<unset>') +
    ' (ENG-8344). This is NOT a GitHub permissions problem.\\n',
  );
  process.exit(1);
}

async function postJson(url, body, headers) {
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: Object.assign({ 'Content-Type': 'application/json' }, headers || {}),
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(15000),
    });
  } catch (err) {
    throw new Error('cannot reach ' + url + ' (' + (err && err.message ? err.message : String(err)) + ')');
  }
  const text = await res.text().catch(() => '');
  if (!res.ok) {
    let detail = text.slice(0, 300);
    try {
      const parsed = JSON.parse(text);
      if (parsed && parsed.error) detail = String(parsed.error);
    } catch { /* keep the raw body */ }
    // The STATUS travels with the error, not just inside its prose. A caller
    // that needs to branch on it (identityMismatchHint does) would otherwise
    // have to regex its own message back apart, and a diagnostic built on
    // string-matching an error it also formats is one wording change from
    // silently never firing again.
    const httpError = new Error(url + ' returned HTTP ' + res.status + (detail ? ': ' + detail : ''));
    httpError.status = res.status;
    throw httpError;
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(url + ' returned a non-JSON body');
  }
}

async function resolveToken() {
  if (!AGT_HOST) fail('AGT_HOST is not set in this process environment');
  if (!AGT_API_KEY) fail('AGT_API_KEY is not set in this process environment');
  if (!AGT_AGENT_ID) fail('AGT_AGENT_ID is not set in this process environment');

  let jwt;
  try {
    const exchanged = await postJson(AGT_HOST + '/host/exchange', { host_key: AGT_API_KEY });
    jwt = exchanged && exchanged.token;
  } catch (err) {
    fail('host key exchange failed - ' + err.message);
  }
  if (!jwt) fail('host key exchange returned no token');

  let credential;
  try {
    credential = await postJson(
      AGT_HOST + '${GITHUB_CREDENTIAL_PATH}',
      { agent_id: AGT_AGENT_ID },
      { Authorization: 'Bearer ' + jwt },
    );
  } catch (err) {
    fail('broker credential fetch failed - ' + err.message + identityMismatchHint(err && err.status));
  }
  const token = credential && credential.access_token;
  if (typeof token !== 'string' || token === '') {
    fail('the broker returned no access_token for the github integration (is it still connected?)');
  }
  return token;
}
`;

const GENERATED_BANNER = `#!/usr/bin/env node
// Auto-generated by Augmented (ENG-8344) — do not edit.
// Canonical source: packages/core/src/provisioning/github-broker-credentials.ts
`;

/**
 * Render the git credential helper.
 *
 * Speaks git's credential protocol: key=value lines on stdin terminated by a
 * blank line, the same shape back on stdout. Only `get` does anything —
 * `store` and `erase` exit 0 silently because there is nothing persisted to
 * store or erase, and a non-zero exit there would make every successful push
 * print a spurious helper error.
 *
 * The host guard is not decoration. A credential helper is asked for whatever
 * host git is talking to, so without it a request for an unrelated private
 * registry would be answered with a GitHub App token — handing a live
 * credential to a third party. Anything that is not github.com exits 0 with no
 * output, which git reads as "this helper has nothing" and moves on.
 */
export function renderGitCredentialHelper(): string {
  return `${GENERATED_BANNER}${TOKEN_RESOLVER_PRELUDE}
function readStdin() {
  return new Promise((resolve) => {
    let buf = '';
    process.stdin.setEncoding('utf8');
    process.stdin.on('data', (chunk) => { buf += chunk; });
    process.stdin.on('end', () => resolve(buf));
    process.stdin.on('error', () => resolve(buf));
  });
}

function parseCredentialRequest(input) {
  const out = {};
  for (const line of String(input).split('\\n')) {
    if (line === '') continue;
    const eq = line.indexOf('=');
    if (eq <= 0) continue;
    out[line.slice(0, eq)] = line.slice(eq + 1);
  }
  return out;
}

async function main() {
  const operation = process.argv[2] || '';
  // Nothing is persisted, so there is nothing to store or erase. Exit clean so
  // a successful push does not print a helper error on the way out.
  if (operation !== 'get') process.exit(0);

  const request = parseCredentialRequest(await readStdin());

  // Only ever answer for github.com over https. Any other host gets silence,
  // which git treats as "this helper has no credential" and falls through to
  // the next one - never a GitHub token handed to an unrelated server.
  const host = request.host || '';
  const protocol = request.protocol || '';
  if (host !== 'github.com' || (protocol && protocol !== 'https')) process.exit(0);

  const token = await resolveToken();
  // x-access-token is the username GitHub expects for an App installation token.
  process.stdout.write(
    'protocol=https\\n' +
    'host=github.com\\n' +
    'username=x-access-token\\n' +
    'password=' + token + '\\n' +
    '\\n',
  );
}

main().catch((err) => fail(err && err.message ? err.message : String(err)));
`;
}

/**
 * Render the `gh` PATH shim.
 *
 * `gh` has no credential-helper concept: it reads `GH_TOKEN` / `GITHUB_TOKEN`
 * from its environment, or a token persisted in `hosts.yml`. The second is the
 * on-disk variant the design review rejected, so the shim takes the first —
 * but only in the CHILD's environment, for the lifetime of that one command.
 * The agent's own environment never holds the token.
 *
 * Finding the real binary is the fiddly part: this file is itself named `gh`
 * and sits first on PATH, so a naive lookup re-executes the shim forever. The
 * search skips any candidate that resolves to this file.
 */
export function renderGhShim(): string {
  return `${GENERATED_BANNER}${TOKEN_RESOLVER_PRELUDE}
const { spawnSync } = require('node:child_process');
const { accessSync, constants, realpathSync } = require('node:fs');
const { join, delimiter } = require('node:path');
const { constants: osConstants } = require('node:os');
const signals = osConstants.signals;

const SELF = (() => {
  try { return realpathSync(__filename); } catch { return __filename; }
})();

/**
 * First executable named 'gh' on PATH that is NOT this shim. Skipping by
 * resolved path (not by directory string) means a symlinked or relocated bin
 * dir cannot trick the search into re-entering the shim.
 */
function findRealGh() {
  for (const dir of (process.env.PATH || '').split(delimiter)) {
    if (!dir) continue;
    const candidate = join(dir, 'gh');
    let resolved;
    try {
      accessSync(candidate, constants.X_OK);
      resolved = realpathSync(candidate);
    } catch { continue; }
    if (resolved === SELF) continue;
    return candidate;
  }
  return null;
}

async function main() {
  const realGh = findRealGh();
  if (!realGh) {
    process.stderr.write(
      'augmented: the real gh binary is not on PATH (only the Augmented shim is). ' +
      'Install gh, or unset the broker credential path.\\n',
    );
    process.exit(127);
  }

  const token = await resolveToken();
  // Token lives ONLY in this child's environment, for this one command.
  const env = Object.assign({}, process.env, {
    GH_TOKEN: token,
    GITHUB_TOKEN: token,
  });
  const result = spawnSync(realGh, process.argv.slice(2), { stdio: 'inherit', env });
  if (result.error) {
    process.stderr.write('augmented: failed to run ' + realGh + ': ' + result.error.message + '\\n');
    process.exit(126);
  }
  // Report a signal death as the shell convention 128+n, with n the REAL signal
  // number from os.constants.signals. \`result.signal\` is a name ('SIGINT'), not
  // a number, so a truthiness check would collapse every signal to 129 and a
  // Ctrl-C would report 129 instead of 130 - wrong in exactly the case a caller
  // inspects the code to tell "interrupted" from "failed". Unknown name falls
  // back to 128, which reads as "died on a signal we could not name".
  if (result.status === null) {
    const number = signals[result.signal];
    process.exit(128 + (typeof number === 'number' ? number : 0));
  }
  process.exit(result.status);
}

main().catch((err) => fail(err && err.message ? err.message : String(err)));
`;
}
